'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var React = require('react');
var TiaAuthClient = require('../utils/TiaAuthClient.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

var AUTH_STATE;
(function (AUTH_STATE) {
    AUTH_STATE["UNAUTHENTICATED"] = "UNAUTHENTICATED";
    AUTH_STATE["LOADING"] = "LOADING";
    AUTH_STATE["AUTHENTICATED"] = "AUTHENTICATED";
})(AUTH_STATE || (AUTH_STATE = {}));
const TiaAuthContext = React__default['default'].createContext(undefined);
const AuthProvider = (props) => {
    const LoadingState = props.LoadingState;
    const UnauthenticatedState = props.UnauthenticatedState;
    const autoLogin = props.autoLogin || false;
    const isCallbackUrl = React.useCallback(() => {
        return `${window.location.origin}${window.location.pathname}` === props.redirectUri;
    }, [props.redirectUri]);
    const authClient = React.useMemo(() => new TiaAuthClient.TiaAuthClient({
        getSignInSilentParams: props.getSignInSilentParams,
        authOptions: {
            domain: props.domain,
            redirectUri: props.redirectUri,
            clientId: props.clientId,
            scope: props.scope,
            successfulLogoutUri: props.logoutUri,
            audience: props.audience,
        },
    }), []);
    const [state, setState] = React.useState({
        authState: AUTH_STATE.LOADING,
        user: null,
        errors: '',
    });
    const { authState, errors, user } = state;
    const login = React.useCallback((args) => {
        storeCurrentUrl();
        authClient.login(args);
    }, [authClient]);
    const logout = React.useCallback((args) => tslib.__awaiter(void 0, void 0, void 0, function* () {
        yield authClient.logout(args);
        yield setAuthState(AUTH_STATE.UNAUTHENTICATED);
    }), [authClient]);
    const setUser = React.useCallback((user) => {
        setState((oldState) => (Object.assign(Object.assign({}, oldState), { user })));
    }, [setState]);
    const setAuthState = React.useCallback((authState, errors = '') => {
        setState((oldState) => (Object.assign(Object.assign({}, oldState), { authState, errors })));
    }, []);
    const authProviderState = React.useMemo(() => ({ authClient, user, logout, login }), [authClient, logout, login, user]);
    const redirectToPreviousUrl = () => {
        window.history.replaceState(undefined, '', getPreviousUrl());
        clearPreviousUrl();
    };
    React.useEffect(() => {
        authClient.getUser().then(setUser);
    }, []);
    React.useLayoutEffect(() => {
        const onUserUpdated = (user) => setUser(user);
        const onUserUnloaded = () => {
            setAuthState(AUTH_STATE.UNAUTHENTICATED);
            onUserUpdated(null);
        };
        authClient.getClientEvents().addUserUnloaded(onUserUnloaded);
        authClient.addOnTokenRenewListener(onUserUpdated);
        props.onSignIn && authClient.addOnSignInListener(props.onSignIn);
        props.onAfterSignIn && authClient.addOnAfterSignInListener(props.onAfterSignIn);
        props.onTokenRenew && authClient.addOnTokenRenewListener(props.onTokenRenew);
        props.onLogout && authClient.addOnLogoutListener(props.onLogout);
        return () => {
            authClient.getClientEvents().removeUserUnloaded(onUserUnloaded);
            authClient.removeOnTokenRenewListener(onUserUpdated);
            props.onSignIn && authClient.removeOnSignInListener(props.onSignIn);
            props.onAfterSignIn && authClient.removeOnAfterSignInListener(props.onAfterSignIn);
            props.onTokenRenew && authClient.removeOnTokenRenewListener(props.onTokenRenew);
            props.onLogout && authClient.removeOnLogoutListener(props.onLogout);
        };
    }, [props.onSignIn, props.onAfterSignIn, props.onTokenRenew, props.onLogout, setAuthState, authClient]);
    React.useEffect(() => {
        (() => tslib.__awaiter(void 0, void 0, void 0, function* () {
            const initialAuthState = yield getInitialAuthState(authClient);
            if (isCallbackUrl()) {
                // Finish auth process
                try {
                    yield authClient.callback();
                    redirectToPreviousUrl();
                    setAuthState(AUTH_STATE.AUTHENTICATED);
                }
                catch (e) {
                    console.error('ERROR', e);
                    redirectToPreviousUrl();
                    setAuthState(AUTH_STATE.UNAUTHENTICATED, e.message);
                }
                return;
            }
            if (initialAuthState === AUTH_STATE.AUTHENTICATED) {
                setAuthState(AUTH_STATE.AUTHENTICATED);
                // Do not do anything more here
                return;
            }
            setAuthState(AUTH_STATE.UNAUTHENTICATED, '');
            if (autoLogin) {
                login();
            }
        }))();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authClient, login, isCallbackUrl]);
    const renderContent = () => {
        switch (authState) {
            case AUTH_STATE.AUTHENTICATED:
                return props.children;
            case AUTH_STATE.LOADING:
                return LoadingState ? React__default['default'].createElement(LoadingState, null) : React__default['default'].createElement(DefaultLoadingState, null);
            case AUTH_STATE.UNAUTHENTICATED:
                return UnauthenticatedState ? (React__default['default'].createElement(UnauthenticatedState, { login: login, errors: errors })) : (React__default['default'].createElement(DefaultUnauthenticatedState, { login: login, errors: errors }));
        }
    };
    return React__default['default'].createElement(TiaAuthContext.Provider, { value: authProviderState }, renderContent());
};
const DefaultLoadingState = () => {
    return React__default['default'].createElement("div", null, "Authenticating...");
};
const DefaultUnauthenticatedState = ({ login, errors }) => {
    return (React__default['default'].createElement("div", null,
        React__default['default'].createElement("div", null, "Unauthenticated"),
        React__default['default'].createElement("div", { style: { color: 'red' } }, errors),
        React__default['default'].createElement("button", { onClick: login }, "Login")));
};
const storeCurrentUrl = () => {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    localStorage.setItem('TIA_AUTH_PREVIOUS_URL', currentPath);
};
const clearPreviousUrl = () => {
    localStorage.removeItem('TIA_AUTH_PREVIOUS_URL');
};
const getPreviousUrl = () => {
    return localStorage.getItem('TIA_AUTH_PREVIOUS_URL');
};
const useTiaAuth = () => {
    const authContext = React__default['default'].useContext(TiaAuthContext);
    if (!authContext) {
        throw new Error('useTiaAuth is expected to be used within AuthProvider component');
    }
    return authContext;
};
const getInitialAuthState = (authClient) => tslib.__awaiter(void 0, void 0, void 0, function* () {
    const isAuthenticated = yield authClient.isAuthenticated();
    return isAuthenticated ? AUTH_STATE.AUTHENTICATED : AUTH_STATE.UNAUTHENTICATED;
});

exports.AuthProvider = AuthProvider;
exports.TiaAuthContext = TiaAuthContext;
exports.useTiaAuth = useTiaAuth;
